'use client';

import { useState } from 'react';

interface MessageInputProps {
  onSendMessage: (text: string) => Promise<void>;
  disabled?: boolean;
}

export default function MessageInput({ onSendMessage, disabled = false }: MessageInputProps) {
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim() || isSending || disabled) return;

    setIsSending(true);
    try {
      await onSendMessage(messageText);
      setMessageText('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const canSend = messageText.trim() && !isSending && !disabled;

  return (
    <form
      onSubmit={handleSubmit}
      className="border-t border-white/[0.08] bg-[#0B0D17]/95 backdrop-blur-sm px-3 sm:px-5 pt-3 sm:pt-4 relative z-10"
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))', boxShadow: '0 -4px 12px rgba(0,0,0,0.3)' }}
    >
      {/* Quick reply suggestions */}
      {messageText.length === 0 && (
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {['Need help with an order', 'Question about pricing', 'Technical issue'].map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => setMessageText(suggestion)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap bg-white/[0.04] border border-white/[0.10] text-gray-400 hover:bg-purple-500/15 hover:border-purple-500/30 hover:text-purple-200 transition-all"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 sm:gap-3 items-end">
        <div className="flex-1 relative">
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isSending || disabled}
            className="w-full resize-none rounded-xl px-4 py-3 pr-12 text-sm text-white placeholder-gray-500 bg-white/[0.03] border border-white/[0.12] focus:outline-none focus:border-purple-500 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition-all"
            style={{ minHeight: '52px', maxHeight: '120px' }}
            rows={1}
          />
          {messageText.length > 0 && (
            <div className="absolute bottom-2 right-2 text-[11px] px-1.5 py-0.5 rounded bg-[#0B0D17]/70" style={{ color: messageText.length > 500 ? '#FCA5A5' : '#6B7280' }}>
              {messageText.length}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSend}
          className={`h-[52px] px-4 sm:px-6 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all flex-shrink-0 ${
            canSend
              ? 'bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white shadow-lg shadow-purple-500/30 hover:-translate-y-0.5'
              : 'bg-white/[0.06] border border-white/[0.10] text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSending ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="hidden sm:inline">Sending</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Send</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </>
          )}
        </button>
      </div>

      <div className="mt-2 hidden sm:flex items-center gap-2 text-xs text-gray-600">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span>Press Enter to send, Shift+Enter for new line</span>
      </div>
    </form>
  );
}
